<script setup lang="ts">
// Thin route-loading bar above the sticky header. Starts when a navigation
// begins (lazy route chunks, auth guard), trickles towards 80 % like
// nprogress, jumps to 100 % when the navigation settles and fades out.
// Navigations that resolve within START_DELAY_MS never show it, so instant
// in-app hops don't flash. Purely visual: aria-hidden.
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const START_DELAY_MS = 120;
const TRICKLE_MS = 300;
const CAP = 80; // never claims more than 80 % until the route has resolved
const FADE_MS = 250;

const router = useRouter();
const width = ref(0);
const visible = ref(false);
let startTimer: number | undefined;
let trickleTimer: number | undefined;
let fadeTimer: number | undefined;

function start() {
  window.clearTimeout(startTimer);
  window.clearTimeout(fadeTimer);
  startTimer = window.setTimeout(() => {
    width.value = 8;
    visible.value = true;
    window.clearInterval(trickleTimer);
    trickleTimer = window.setInterval(() => {
      width.value += (CAP - width.value) * 0.18;
    }, TRICKLE_MS);
  }, START_DELAY_MS);
}

function done() {
  window.clearTimeout(startTimer);
  window.clearInterval(trickleTimer);
  if (!visible.value) return;
  width.value = 100;
  fadeTimer = window.setTimeout(() => {
    visible.value = false;
    fadeTimer = window.setTimeout(() => (width.value = 0), FADE_MS);
  }, FADE_MS);
}

let removers: (() => void)[] = [];
onMounted(() => {
  removers = [
    router.beforeEach(() => { start(); }),
    router.afterEach(() => done()),
    router.onError(() => done()),
  ];
});
onBeforeUnmount(() => {
  removers.forEach((off) => off());
  window.clearTimeout(startTimer);
  window.clearInterval(trickleTimer);
  window.clearTimeout(fadeTimer);
});
</script>

<template>
  <div
    aria-hidden="true"
    class="pointer-events-none fixed inset-x-0 top-0 z-[80] h-0.5 transition-opacity duration-250"
    :class="visible ? 'opacity-100' : 'opacity-0'"
  >
    <div
      class="h-full bg-brand-gradient shadow-glow transition-[width] duration-300 ease-out motion-reduce:transition-none"
      :style="{ width: `${width}%` }"
    ></div>
  </div>
</template>
